// src/components/Features.js
import React, { useState } from 'react';
import { useInView } from 'react-intersection-observer';

const features = [
    {
    icon: "",
    title: "Zero Scamalytics Score",
    description: "Our proxies are completely undetectable by Scamalytics, ensuring maximum security.",
    color: "from-green-500/20 to-transparent"
  },
  {
    icon: "",
    title: "100% Uptime Guarantee",
    description: "Experience uninterrupted service with our reliable infrastructure and redundant systems.",
    color: "from-blue-500/20 to-transparent"
  },
    {
    icon: "",
    title: "Global Coverage",
    description: "Connect through 190+ countries and 3,000+ cities worldwide for true global reach.",
    color: "from-pink-500/20 to-transparent"
  },
  {
    icon: "",
    title: "Instant Delivery",
    description: "Get instant access to your proxies as soon as you purchase, with automated delivery system.",
    color: "from-orange-500/20 to-transparent"
  },

  {
    icon: "",
    title: "Competitive Pricing",
    description: "Access premium proxy services starting at just 1$ for Static Proxies.",
    color: "from-purple-500/20 to-transparent"
  },

  {
    icon: "",
    title: "24/7 Support",
    description: "Our technical support team is available round the clock to assist you with any issues.",
    color: "from-yellow-500/20 to-transparent"
  }
];

const FeatureCard = ({ feature, index, isExpanded }) => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  return (
    <div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5, delay: isExpanded ? index * 0.1 : 0 }}
      className="relative group"
    >
      <div className="absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl -z-10">
        <div className={`absolute inset-0 bg-gradient-to-br ${feature.color}`} />
      </div>
      
      <div className="glass-effect p-6 md:p-8 rounded-2xl hover:border-accent/50 transition-colors duration-300">
        {/* Icon */}
        <div
          initial={{ scale: 0 }}
          animate={inView ? { scale: 1 } : {}}
          transition={{ duration: 0.5, delay: index * 0.1 + 0.2 }}
          className="text-4xl md:text-5xl mb-4"
        >
          {feature.icon}
        </div>

        {/* Content */}
        <h3 className="text-xl md:text-2xl font-bold mb-3 text-text-primary">
          {feature.title}
        </h3>
        <p className="text-text-secondary text-sm md:text-base">
          {feature.description}
        </p>

        {/* Hover Effect */}
        <div
          className="absolute inset-0 border-2 border-accent/0 rounded-2xl transition-colors duration-300 group-hover:border-accent/50"
          initial={false}
          animate={inView ? { scale: 1 } : { scale: 0.95 }}
        />
      </div>
    </div>
  );
};

const Features = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const visibleFeatures = isExpanded ? features : features.slice(0, 3);

  return (
    <section className="py-16 md:py-24 relative" id="features">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-1/2 h-1/2 bg-accent/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-1/2 h-1/2 bg-purple-500/5 rounded-full blur-[120px]" />
      </div>

      <div className="container mx-auto px-4" ref={ref}>
        {/* Section Header */}
        <div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 md:mb-20"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            Core Features of <span className="text-accent">NexaPx</span>
          </h2>
          <p className="text-text-secondary text-sm md:text-base max-w-2xl mx-auto">
            Experience the next generation of proxy services with our cutting-edge features
            designed for maximum performance and reliability.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">

            {visibleFeatures.map((feature, index) => (
              <FeatureCard
                key={feature.title}
                feature={feature}
                index={index}
                isExpanded={isExpanded}
              />
            ))}

        </div>


{/* Bottom CTA */}
<div
  initial={{ opacity: 0, y: 20 }}
  animate={inView ? { opacity: 1, y: 0 } : {}}
  transition={{ duration: 0.6, delay: 0.4 }}
  className="text-center mt-12 md:mt-20 relative z-10" // Added relative and z-10
>
  <button
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
    onClick={() => setIsExpanded(!isExpanded)}
    className="bg-accent hover:bg-accent-hover text-dark-bg px-8 py-3 rounded-xl font-medium 
              inline-flex items-center space-x-2 cursor-pointer relative z-10" // Added cursor-pointer and relative z-10
  >
    <span className="relative z-10">{isExpanded ? 'Show Less' : 'Explore All Features'}</span>
    <svg
      className={`w-5 h-5 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''} relative z-10`} // Added relative z-10
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      {isExpanded ? (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5 15l7-7 7 7"
        />
      ) : (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 9l-7 7-7-7"
        />
      )}
    </svg>
  </button>
</div>


      </div>
    </section>
  );
};

export default Features;